/**
 * Legal requirements of the printed work order, per country.
 *
 * The workshop picks its country in Settings (legalCountry). Each profile says
 * what the official sheet must contain; the texts themselves live in the locale
 * files under `legal.<textKey>.*`, so a new country is a new profile plus its
 * texts. Countries without a profile use the generic one (no statutory
 * warranty block, neutral intake text).
 */
import { toMinor } from "./money";

export interface LegalProfile {
  /** ISO 3166 code, or "OTHER" for the generic profile. */
  country: string;
  /** Prefix of the texts in the locale files: legal.<textKey>.intake, .warranty */
  textKey: string;
  /** Mandatory consumer warranty for vehicle repairs, or null if the country has none. */
  consumerWarranty: {
    /** The warranty applies when the gross fee (labour and parts) exceeds this amount. */
    thresholdAmount: number;
    thresholdCurrency: string;
    months: number;
  } | null;
  /** The intake receipt must show the expected completion date. */
  intakeNeedsExpectedDate: boolean;
}

export const GENERIC_LEGAL_PROFILE: LegalProfile = {
  country: "OTHER",
  textKey: "generic",
  consumerWarranty: null,
  intakeNeedsExpectedDate: false,
};

export const LEGAL_PROFILES: readonly LegalProfile[] = [
  // 249/2004. (VIII. 27.) Korm. rendelet: vehicle repairs for consumers above
  // 20 000 Ft (with VAT and materials) carry 6 months warranty from handover;
  // the intake receipt shows the expected completion date (5. §).
  {
    country: "HU",
    textKey: "HU",
    consumerWarranty: { thresholdAmount: 20000, thresholdCurrency: "HUF", months: 6 },
    intakeNeedsExpectedDate: true,
  },
  GENERIC_LEGAL_PROFILE,
];

export const LEGAL_COUNTRIES: readonly string[] = LEGAL_PROFILES.map((p) => p.country);

export function legalProfile(country: string | null | undefined): LegalProfile {
  return LEGAL_PROFILES.find((p) => p.country === country) ?? GENERIC_LEGAL_PROFILE;
}

export interface WarrantyCase {
  /** Customer is a consumer (not a company). */
  consumer: boolean;
  /** Gross total of the work order in minor units of `currency`. */
  grossMinor: number;
  currency: string;
}

/**
 * True when the closed sheet must carry the statutory warranty card.
 * A total in another currency than the threshold's is treated as above it
 * (printing the card when not needed does no harm, missing it does).
 */
export function warrantyRequired(profile: LegalProfile, c: WarrantyCase): boolean {
  const w = profile.consumerWarranty;
  if (!w || !c.consumer) return false;
  if (c.currency !== w.thresholdCurrency) return true;
  return c.grossMinor > toMinor(w.thresholdAmount, w.thresholdCurrency);
}

/** Last day of the warranty: handover date plus the given months (YYYY-MM-DD). */
export function warrantyUntil(handoverIso: string, months: number): string {
  const [y, m, d] = handoverIso.slice(0, 10).split("-").map(Number) as [number, number, number];
  const target = new Date(Date.UTC(y, m - 1 + months, 1));
  const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(d, lastDay));
  return target.toISOString().slice(0, 10);
}
